<template>
    <template v-if="loadingError">
        <div class="fs-5 text-primary d-inline-flex">
            {{ loadingError }}
        </div>
    </template>
    <template v-if="identity">
        <HeaderSection :identity="identity" />
        <div class="vff vff-not-standalone custom">
            <div class="f-container">
                <div class="f-form-wrap">
                    <div class="vff-animate q-form q-is-active f-fade-in-up">
                        <Breadcrumb v-if="breadcrumb && breadcrumb?.length > 0"
                                    class="mb-2"
                                    :items="breadcrumb" />
                        <slot v-if="hasNamedSlot('title')"
                              name="title" />
                        <TitleSection v-else
                                      :title="title"
                                      @previousStepClick="$emit('previousStepClick')"
                                      :previous-step-route="previousStepRoute" />

                        <slot />
                    </div>
                </div>
            </div>
        </div>

        <FooterSection :identity="identity" />
        <Transition name="slide-in slide-out">
            <ConsentQuestion v-if="showCookieConsentCompliance"
                             @cookie-compliance="hasCookieConsent = true" />
        </Transition>
    </template>
</template>
<script lang="ts">
import { defineComponent, onBeforeMount, computed, ref } from 'vue'
import HeaderSection from '~concierge/components/HeaderSection.vue'
import FooterSection from '~concierge/components/FooterSection.vue'
import TitleSection from '~concierge/components/TitleSection.vue'
import ConsentQuestion from '~concierge/components/ConsentQuestion.vue'
import { IdentityService } from '~concierge/services/IdentityService'
import { IdentityData } from '~concierge/types/IdentityTypes'
import { useIdentityStore } from '~concierge/store/identityStore'
import { useHead } from '@unhead/vue'
import { usePage } from '@inertiajs/vue3'
import { useMobileDetection } from '~concierge/composables/deviceDetection'
import Breadcrumb from './Breadcrumb.vue'
import { BreadcrumbResource } from '~types/concierge'
import { trans } from 'laravel-vue-i18n'

export default defineComponent({
    components: { HeaderSection, FooterSection, TitleSection, ConsentQuestion, Breadcrumb },
    props: {
        title: {
            type: String,
            required: true
        },
        previousStepRoute: {
            type: String,
            default: ''
        },
        longContent: {
            type: Boolean,
            default: false
        },
        breadcrumb: {
            type: Array as () => { [key: number]: BreadcrumbResource },
            required: false
        }
    },
    setup(props, { slots }) {
        const hasNamedSlot = (name: string): boolean => {
            return !!slots[name]
        }
        const page = usePage()
        const { isIos, isMobile } = useMobileDetection()
        const hasCookieConsent = ref<boolean>(false)
        const requiredCookieConsent = computed((): boolean => page.props.requiredCookieConsent as boolean)

        const showCookieConsentCompliance = computed((): boolean => {
            return requiredCookieConsent.value && !hasCookieConsent.value
        })

        const identity = ref<IdentityData | null>(null)
        const identityStore = useIdentityStore()
        const loadingError = ref<string | null>(null)
        onBeforeMount(async () => {
            try {
                const appStatus = await IdentityService.getAppStatus()

                if (appStatus.redirectToMaintenancePage) {
                    return (window.location.href = '/alfred-form-maintenance')
                }
            } catch (error) {
                console.error('Unable to fetch concierge app status', error)
                loadingError.value = trans('messages.loading_error')
                return
            }

            try {
                const identityData = page.props.identity as IdentityData
                identityStore.setIdentity(identityData)
                identity.value = identityStore.identity
                for (const property in identity.value?.theme) {
                    document
                        .querySelector('body')
                        ?.style.setProperty(property, identity.value?.theme[property])
                }

                const faviconObject = {
                    rel: 'shortcut icon',
                    type: '',
                    href: ''
                }
                if (identityStore.identity?.favicon && identityStore.identity?.favicon?.length > 0) {
                    document.head.querySelector("[rel='icon']")?.remove()
                    faviconObject['href'] = identityStore.identity?.favicon
                    faviconObject['type'] = 'image/png'
                } else if (identityStore.identity?.logoUrl && identityStore.identity?.logoUrl?.length > 0) {
                    const encodedSVG = identityStore.identity?.logoUrl
                        ?.replace(/\"/g, '%22')
                        .replace(/\#/g, '%23')
                    document.head.querySelector("[rel='icon']")?.remove()
                    faviconObject['href'] = 'data:image/svg+xml,' + encodedSVG
                    faviconObject['type'] = 'image/svg+xml'
                }
                useHead({
                    title: identityStore.identity?.title,
                    link: [faviconObject]
                })
            } catch (error) {
                console.error('Unable to fetch concierge identity setup', error)
                loadingError.value = trans('messages.loading_error')
            }
        })

        const loaderElement = document.getElementById('loader')
        if (loaderElement) {
            loaderElement.style.display = 'none'
        }

        return {
            showCookieConsentCompliance,
            hasNamedSlot,
            isIos,
            isMobile,
            identity,
            loadingError,
            hasCookieConsent
        }
    },
    watch: {
        identity: {
            immediate: true,
            handler() {
                if (this.identity) {
                    // if (this.longContent) {
                    document.body.className = `${this.bodyBaseClass} ${this.bodyBaseClass}--long-content`
                    // } else {
                    //     document.body.className = this.bodyBaseClass
                    // }
                }
            }
        },

        longContent: {
            immediate: true,
            handler() {
                // if (this.longContent) {
                document.body.className = `${this.bodyBaseClass} ${this.bodyBaseClass}--long-content`
                // } else {
                //     document.body.className = this.bodyBaseClass
                // }
            }
        }
    },
    emits: ['previousStepClick'],
    computed: {
        isDesktop() {
            return !this.isIos && !this.isMobile
        },
        bodyBaseClass() {
            if (this.isDesktop) {
                return 'desktop'
            } else {
                return 'mobile'
            }
        }
    }
})
</script>

<style lang="scss">
@use '~concierge/styles/variables' as *;
@import '~concierge-style/index.scss';
@import '~concierge-style/vue-flow-form/themes/theme-infernal.scss';

.slide-in-enter-active,
.slide-in-leave-active {
    transition: all 0.3s ease-in-out;
}

.slide-in-enter-from,
.slide-in-leave-to {
    opacity: 0;
    transform: translateY(-100px);
}

.slide-out-enter-active,
.slide-out-leave-active {
    transition: all 0.3s ease-in-out;
}

.slide-out-enter-from,
.slide-out-leave-to {
    opacity: 0;
    transform: translateY(100px);
}

html {
    font-size: 62.5% !important;
}

#app {
    font-feature-settings: 'lnum';
    font-size: var(--dealer-font-size);
    line-height: 1.6;
    font-family: var(--dealer-font-family);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    color: var(--dealer-text-color);
    min-height: 100vh;
    max-width: 100vw;
    display: grid;
    grid-template-rows: min-content auto min-content;
    grid-template-columns: 1fr;
}

body.mobile #app {
    max-width: 100svw;
    grid-template-columns: 100svw;

    .vff {
        max-width: 100vw;
        margin: 0 auto;
    }

    .vff .field-multiplepicturechoice ul.f-radios li {
        max-height: clamp(300px, 33vh, 40dvh);
    }
}

body.desktop {
    display: flex;
    place-content: center;
    place-items: self-start;
    margin-top: 3vh;
    margin-bottom: 3vh;
    min-height: 100vh;
    background: var(--bg-img, white) rgba(19, 19, 19, 0.18);
    background-blend-mode: overlay;
    background-size: cover;
    background-position: center center;
    background-attachment: fixed;
    opacity: 1;
}

body.desktop #app {
    max-width: unset;
    max-height: unset;
    height: clamp(60vh, 721px, 95vh);
    width: clamp(600px, 1000px, 80vw);
    min-height: unset;
    min-width: unset;
    grid-template-rows: min-content minmax(600px, 80vh) min-content;

    .vff .field-multiplepicturechoice ul.f-radios {
        display: grid;
        grid-template-columns: repeat(3, 1fr);

        li {
            flex: unset;
        }
    }
}

body.desktop.desktop--long-content #app {
    grid-template-rows: min-content max-content min-content;
}

.img-container {
    max-width: 100%;
    height: auto;

    img {
        resize: both;
        width: 100%;
        height: auto;
    }
}

a {
    font-weight: bold;
    color: var(--dealer-link-color);
}

a.router-link-exact-active {
    color: var(--dealer-link-color);
}

header {
    background-color: var(--dealer-header-background);
    color: var(--dealer-header-text);
    padding: 3% 1%;

    a {
        font-weight: bold;
        color: var(--dealer-header-link);

        &.router-link-exact-active {
            color: var(--dealer-header-link);
        }
    }
}

footer {
    background-color: var(--dealer-footer-background);
    color: var(--dealer-footer-text);
    padding: 2% 1%;
}

.kb-answer {
    font-size: var(--font-size-small);
    font-weight: 400;

    table {
        @extend .table;
        @extend .table-responsive;
        @extend .table-bordered;
        --bs-table-bg: transparent;

        tr:first-child td {
            font-weight: 600;
        }
    }

    a {
        border-bottom: 1px solid;
        border-color: inherit;
        padding-bottom: 0.02em;
        transition: border-bottom-color 0.4s ease-in-out, color 0.3s ease-in-out;

        &:hover {
            border-bottom-color: transparent;
        }
    }
}

.unavailable-selection-notification {
    @extend .col-12;

    margin: 1em auto;
    font-weight: 500;
    display: grid;
    grid-template-columns: 2em auto;

    @include media-breakpoint-down(lg) {
        grid-template-columns: minmax(2em, 3em) auto;
    }

    &__icon {
        border-top-left-radius: var(--vff-border-radius);
        border-bottom-left-radius: var(--vff-border-radius);
        background-color: var(--dealer-primary-color);
        color: var(--dealer-background-color);
        display: flex;
        place-content: center;
        place-items: center;
        // aspect-ratio: 1 /1;

        @include media-breakpoint-down(lg) {
            min-height: 100%;
        }
    }

    &__text {
        @extend .fs-5;
        border-top-right-radius: var(--vff-border-radius);
        border-bottom-right-radius: var(--vff-border-radius);
        padding: 0.3em 0.6em;
        background-color: var(--dealer-secondary-light);
        color: var(--dealer-text-color);
        display: flex;
        flex-direction: column;
        justify-content: center;
    }
}

.button-next.btn.btn-primary,
.button-prev.btn.btn-primary {
    --bs-btn-color: var(--carousel-nav-btn-text-color, #fff);
    --bs-btn-bg: var(--carousel-nav-btn-bg-color, var(--dealer-primary-color));
    --bs-btn-hover-color: var(--carousel-nav-btn-hover-text-color, #fff);
    --bs-btn-hover-bg: var(--carousel-nav-btn-hover-bg-color, var(--dealer-primary-color));
    --bs-btn-hover-border-color: var(--carousel-nav-btn-hover-border-color, var(--dealer-primary-color));
}
</style>
